import { useTheme } from '@parkdna/ui/theme';
import { Helmet } from 'react-helmet';
import { useMatches } from 'react-router-dom';

import { useAuth } from '@/hooks/auth/useAuth';
import { capitalize } from '@/utils/primitives';

export default function DynamicHead() {
  const { orgName, orgMetadata } = useAuth();
  const [theme] = useTheme();
  const matches = useMatches();

  const description = `Welcome to ${orgName ?? ''} – Your Ultimate Tool for Efficient Parking Management. Exclusively designed for parking lot operators, our portal provides a comprehensive platform to review transactional data, generate insightful reports, and optimize your parking operations. Access real-time analytics, manage financials, and elevate your parking business with ease. Log in now to transform your parking data into actionable intelligence.`;
  const faviconSvgUrl = orgMetadata?.['favicon_svg_url'] ?? '/favicon.svg';

  let pageTitleSuffix: string | undefined;
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i] as {
      handle?: {
        title?: string | ((params: Record<string, string>) => string);
      };
      params: Record<string, string>;
    };
    const handleTitle = match?.handle?.title;
    if (typeof handleTitle === 'function') {
      pageTitleSuffix = handleTitle(match.params);
      break;
    }
    if (typeof handleTitle === 'string' && handleTitle) {
      pageTitleSuffix = handleTitle;
      break;
    }
  }

  const title = `${capitalize(orgName ?? '')} Operator Portal${
    pageTitleSuffix ? ` - ${pageTitleSuffix}` : ''
  }`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="theme-color"
        media="(prefers-color-scheme: light)"
        content={theme.palette.primary.light}
      />
      <meta
        name="theme-color"
        media="(prefers-color-scheme: dark)"
        content={theme.palette.primary.dark}
      />
      <meta property="og:description" content={description} />
      <link rel="icon" href={orgMetadata?.['favicon_ico_url']} sizes="48x48" />
      <link
        rel="icon"
        href={orgMetadata?.['favicon_svg_url'] ?? '/favicon.svg'}
        sizes="any"
      />
      <meta property="og:image" content={faviconSvgUrl} />
      <meta property="og:title" content={title} />
    </Helmet>
  );
}
